import { ReactNode, useEffect, useState } from 'react'
import cn from 'classnames'
import {
   StepFooter,
   StepHeading,
} from '@forwardflow/features/campaign-flow/components/frame/FlowFrame'
import { AiOrb } from '@forwardflow/features/campaign-flow/components/ui/AiOrb'
import { FlowIcon } from '@forwardflow/features/campaign-flow/components/ui/FlowIcon'
import {
   Checkbox,
   Pill,
   Radio,
   Spinner,
} from '@forwardflow/features/campaign-flow/components/ui/primitives'
import {
   useFill,
   useVoicePreview,
} from '@forwardflow/features/campaign-flow/hooks/useFlowHelpers'
import {
   IAreaCode,
   ICostLine,
   IFlowDraft,
   IPhoneNumberOption,
   IReadinessItem,
   ITeammate,
   IVoiceOption,
   TRailKey,
   TRoutingMode,
} from '@forwardflow/features/campaign-flow/interfaces/campaign-flow.types'
import { campaignFlowService } from '@forwardflow/features/campaign-flow/services'
import { useCampaignFlowStore } from '@forwardflow/features/campaign-flow/store/flow.store'
import { useFlowData } from '@forwardflow/features/campaign-flow/store/flow-data.store'
import {
   cadenceSummary,
   counts,
} from '@forwardflow/features/campaign-flow/utils/playbook'
import {
   numberForCode,
   suggestedCodes,
} from '@forwardflow/features/campaign-flow/utils/numbers'
import { plural } from '@forwardflow/features/campaign-flow/utils/text'

interface IStepProps {
   onBack?: () => void
   onDone: () => void
   busy: boolean
}

const Body = ({
   children,
   width = 'max-w-[620px]',
}: {
   children: ReactNode
   width?: string
}) => (
   <div
      className={cn(
         'px-5 md:px-8 pt-6 pb-4 flex flex-col gap-4 w-full mx-auto',
         width
      )}
   >
      {children}
   </div>
)

/* ---------- Question 2: who takes a hot lead ---------- */

const MODES: { key: TRoutingMode; title: string; blurb: string }[] = [
   {
      key: 'round_robin',
      title: 'Pass it to my team, in turns',
      blurb: 'Each hot lead goes to the next person on the list.',
   },
   {
      key: 'first_to_answer',
      title: 'Ring my team, first to pick up',
      blurb: 'Everyone selected rings at once.',
   },
]

export const HandoffStep = ({ onBack, onDone, busy }: IStepProps) => {
   const { draft, update } = useCampaignFlowStore()
   const [team, setTeam] = useState<ITeammate[] | null>(null)
   useEffect(() => {
      void campaignFlowService
         .teammates()
         .catch(() => [] as ITeammate[])
         .then((list) => {
            setTeam(list)
            /* Everyone is in by default; untick to leave someone out. */
            update((d) =>
               d.handoff.agentIds.length
                  ? d
                  : {
                       ...d,
                       handoff: {
                          ...d.handoff,
                          agentIds: list.map((t) => t.id),
                       },
                    }
            )
         })
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [])
   const h = draft.handoff
   const setMode = (mode: TRoutingMode) =>
      update((d) => ({
         ...d,
         handoff: {
            ...d.handoff,
            mode,
            agentIds:
               d.handoff.agentIds.length || !team
                  ? d.handoff.agentIds
                  : team.map((t) => t.id),
         },
      }))
   const toggle = (id: number) =>
      update((d) => ({
         ...d,
         handoff: {
            ...d.handoff,
            agentIds: d.handoff.agentIds.includes(id)
               ? d.handoff.agentIds.filter((x) => x !== id)
               : [...d.handoff.agentIds, id],
         },
      }))
   const ok = h.agentIds.length > 0

   return (
      <>
         <Body>
            <StepHeading
               icon="phone"
               title="When a lead wants to talk, what happens?"
               lede="Booked appointments also land on your calendar."
            />
            <div className="flex flex-col gap-2" data-testid="cf-handoff">
               {MODES.map((m) => (
                  <button
                     key={m.key}
                     type="button"
                     onClick={() => setMode(m.key)}
                     data-testid={`cf-mode-${m.key}`}
                     className={cn(
                        'rounded-xl border px-4 py-3 flex gap-3 items-center text-left transition-colors',
                        h.mode === m.key
                           ? 'border-[#9061DA] bg-[#9061DA]/[0.14]'
                           : 'border-white/[0.06] bg-white/[0.04] hover:border-white/15'
                     )}
                  >
                     <Radio on={h.mode === m.key} />
                     <span>
                        <span className="block text-[15px] font-semibold">
                           {m.title}
                        </span>
                        <span className="block text-[13px] text-[#9ca3af]">
                           {m.blurb}
                        </span>
                     </span>
                  </button>
               ))}
            </div>
            {
               <div
                  className="rounded-xl bg-white/[0.04] border border-white/[0.06]"
                  data-testid="cf-team"
               >
                  <div className="flex items-center justify-between px-4 h-10 border-b border-white/[0.06] text-[13px]">
                     <b className="font-semibold">Who</b>
                  </div>
                  {!team ? (
                     <div className="flex justify-center py-4">
                        <Spinner />
                     </div>
                  ) : (
                     team.map((t) => (
                        <button
                           key={t.id}
                           type="button"
                           onClick={() => toggle(t.id)}
                           className="w-full flex items-center gap-3 px-4 h-11 border-b border-white/[0.05] last:border-0 text-left hover:bg-white/[0.03]"
                           data-testid={`cf-agent-${t.id}`}
                        >
                           <Checkbox on={h.agentIds.includes(t.id)} />
                           <span className="w-7 h-7 rounded-full bg-[#3b3b44] flex items-center justify-center text-[11px] font-semibold">
                              {t.initials}
                           </span>
                           <span className="text-sm flex-1">{t.name}</span>
                           <span className="text-xs text-[#818181]">
                              {t.role}
                           </span>
                        </button>
                     ))
                  )}
               </div>
            }
         </Body>
         <StepFooter
            onBack={onBack}
            primary={ok ? 'Continue' : 'Pick at least one person'}
            disabled={!ok}
            onPrimary={onDone}
            busy={busy}
         />
      </>
   )
}

/* ---------- Question 3: voice ---------- */

/** $2 · $1.15 */
const money = (n: number) =>
   Number.isInteger(n) ? `$${n}` : `$${n.toFixed(2)}`

export const VoiceStep = ({ onBack, onDone, busy }: IStepProps) => {
   const org = useFlowData((s) => s.org)
   const { draft, update } = useCampaignFlowStore()
   const [voices, setVoices] = useState<IVoiceOption[] | null>(null)
   const fill = useFill()
   const { play, playingId, failedId } = useVoicePreview()
   useEffect(() => {
      void campaignFlowService
         .voices()
         .catch(() => [] as IVoiceOption[])
         .then(setVoices)
   }, [])
   if (!voices)
      return (
         <>
            <div className="flex items-center justify-center py-24">
               <Spinner size={22} />
            </div>
            <StepFooter
               onBack={onBack}
               primary="Loading voices"
               disabled
               onPrimary={onDone}
            />
         </>
      )
   const i = Math.max(
      0,
      voices.findIndex((v) => v.id === draft.voice.optionId)
   )
   const v = voices[i]
   const prev = voices[(i - 1 + voices.length) % voices.length]
   const next = voices[(i + 1) % voices.length]
   const choose = (o: IVoiceOption) =>
      update((d) => {
         const old = voices.find((x) => x.id === d.voice.optionId)
         return {
            ...d,
            voice: {
               optionId: o.id,
               name:
                  !d.voice.name || d.voice.name === old?.defaultName
                     ? o.defaultName
                     : d.voice.name,
            },
         }
      })
   const name = draft.voice.name
   const arrow =
      'w-10 h-10 rounded-full bg-white/[0.08] hover:bg-white/[0.14] flex items-center justify-center shrink-0'

   return (
      <>
         <Body>
            <StepHeading
               icon="voice"
               title="Pick the voice your leads hear"
               lede="Press play to hear a sample."
            />
            <div
               className="flex items-center justify-center gap-3 md:gap-5 mt-2"
               data-testid="cf-voice"
            >
               <button
                  type="button"
                  onClick={() => choose(prev)}
                  className={arrow}
                  aria-label="Previous voice"
                  data-testid="cf-voice-prev"
               >
                  <FlowIcon name="arrowLeft" size={16} />
               </button>
               <button
                  type="button"
                  onClick={() => choose(prev)}
                  className="opacity-40 hover:opacity-70 hidden sm:block"
                  aria-label={prev.style}
               >
                  <AiOrb hue={prev.hue} size={56} animated={false} />
               </button>
               <div className="flex flex-col items-center gap-1 w-[170px]">
                  <AiOrb hue={v.hue} size={112} talking={playingId === v.id} />
                  <span
                     className="text-[17px] font-semibold mt-2"
                     data-testid="cf-voice-name"
                  >
                     {name || v.defaultName}
                  </span>
                  <span className="text-xs text-[#9ca3af]">{v.style}</span>
               </div>
               <button
                  type="button"
                  onClick={() => choose(next)}
                  className="opacity-40 hover:opacity-70 hidden sm:block"
                  aria-label={next.style}
               >
                  <AiOrb hue={next.hue} size={56} animated={false} />
               </button>
               <button
                  type="button"
                  onClick={() => choose(next)}
                  className={arrow}
                  aria-label="Next voice"
                  data-testid="cf-voice-next"
               >
                  <FlowIcon name="arrowRight" size={16} />
               </button>
            </div>
            <div className="flex items-center justify-center gap-2">
               {v.bestPerforming && (
                  <Pill tone="good">
                     <FlowIcon name="trend" size={12} /> Books the most
                  </Pill>
               )}
               {v.isNew && (
                  <Pill tone="accent" testId="cf-voice-new">
                     New voice
                  </Pill>
               )}
               {v.previewUrl ? (
                  <button
                     type="button"
                     onClick={() =>
                        void play(
                           v.id,
                           fill(draft.playbook.script[0]?.text ?? '')
                        )
                     }
                     className="inline-flex items-center gap-1.5 h-8 px-3.5 rounded-full bg-white/[0.08] hover:bg-white/[0.14] text-[13px] font-semibold"
                     data-testid="cf-voice-play"
                  >
                     <FlowIcon
                        name={playingId === v.id ? 'pause' : 'play'}
                        size={11}
                     />{' '}
                     {playingId === v.id ? 'Stop' : 'Play'}
                  </button>
               ) : (
                  <span
                     className="text-xs text-[#9ca3af]"
                     data-testid="cf-voice-no-sample"
                  >
                     No sample for this voice yet
                  </span>
               )}
            </div>
            {failedId === v.id && (
               <p
                  className="text-center text-xs text-amber-300"
                  data-testid="cf-voice-play-error"
               >
                  The sample could not be played. Check your sound and try
                  again.
               </p>
            )}
            <label className="w-full max-w-[360px] mx-auto mt-1 flex flex-col gap-1.5 text-center">
               <span className="text-[13px] text-[#9ca3af]">
                  It introduces itself as
               </span>
               <input
                  value={name}
                  onChange={(e) =>
                     update((d) => ({
                        ...d,
                        voice: { ...d.voice, name: e.target.value },
                     }))
                  }
                  className="w-full h-12 rounded-full bg-white/[0.06] border border-white/[0.12] text-center text-[15px] outline-none focus:border-[#9061DA]"
                  data-testid="cf-voice-name-field"
               />
               <span className="text-xs text-[#818181]">
                  “Hi, this is {name || v.defaultName} from {org.organization}.”
               </span>
            </label>
         </Body>
         <StepFooter
            onBack={onBack}
            primary={
               name.trim() ? `Continue with ${name.trim()}` : 'Give it a name'
            }
            disabled={!name.trim()}
            onPrimary={onDone}
            busy={busy}
         />
      </>
   )
}

/* ---------- Optional: phone numbers ---------- */

/** The draft with these numbers; the area codes follow from them. */
const withNumbers = (d: IFlowDraft, sel: IPhoneNumberOption[]): IFlowDraft => ({
   ...d,
   numbers: {
      selected: sel,
      areaCodes: Array.from(new Set(sel.map((s) => s.areaCode))),
   },
})

/**
 * Phone numbers, by area code. "Where your leads are" lists the codes most
 * of this audience's leads live in; ticking one adds a number there (a spare
 * one the organization owns first, else a new one), unticking removes it.
 * The campaign starts with the busiest codes ticked. The search adds numbers
 * anywhere else.
 */
export const NumbersStep = ({ onDone, busy }: IStepProps) => {
   const price = useFlowData((s) => s.numberPrice)
   const { draft, update } = useCampaignFlowStore()
   const [codes, setCodes] = useState<IAreaCode[] | null>(null)
   const [owned, setOwned] = useState<IPhoneNumberOption[]>([])
   const [query, setQuery] = useState('')
   const [results, setResults] = useState<IPhoneNumberOption[] | null>(null)
   const [searching, setSearching] = useState(false)
   /* Numbers ticked in the search; they join "Your numbers" on Add. */
   const [staged, setStaged] = useState<IPhoneNumberOption[]>([])
   /* Area codes whose number is being looked up after a tick, and ones with none for sale. */
   const [adding, setAdding] = useState<string[]>([])
   const [soldOut, setSoldOut] = useState<string[]>([])
   const selected = draft.numbers.selected
   const isOn = (n: IPhoneNumberOption) =>
      selected.some((s) => s.number === n.number)
   const isStaged = (n: IPhoneNumberOption) =>
      staged.some((s) => s.number === n.number)

   const add = (list: IPhoneNumberOption[]) =>
      update((d) =>
         withNumbers(d, [
            ...d.numbers.selected,
            ...list.filter(
               (n) => !d.numbers.selected.some((s) => s.number === n.number)
            ),
         ])
      )
   const keep = (test: (n: IPhoneNumberOption) => boolean) =>
      update((d) => withNumbers(d, d.numbers.selected.filter(test)))

   /** A spare owned number in that code, else the first one for sale there. */
   const findFor = async (
      code: string,
      ownedList: IPhoneNumberOption[],
      taken: IPhoneNumberOption[]
   ) =>
      numberForCode(code, ownedList, taken) ??
      (
         await campaignFlowService
            .availableNumbers(code)
            .catch(() => [] as IPhoneNumberOption[])
      ).find((n) => n.areaCode === code)

   useEffect(() => {
      let live = true
      void Promise.all([
         campaignFlowService
            .areaCodes(draft.audience, { discover: true })
            .catch(() => [] as IAreaCode[]),
         campaignFlowService
            .ownedNumbers()
            .catch(() => [] as IPhoneNumberOption[]),
      ]).then(async ([c, o]) => {
         if (!live) return
         setCodes(c)
         setOwned(o)
         /* Nothing chosen yet: one number in each code most leads live in, owned ones first. */
         if (
            useCampaignFlowStore.getState().draft.numbers.selected.length ||
            !c.length
         )
            return
         const picks: IPhoneNumberOption[] = []
         for (const code of suggestedCodes(c)) {
            const n = await findFor(code.code, o, picks)
            if (n) picks.push(n)
         }
         if (live && picks.length) add(picks)
      })
      return () => {
         live = false
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [])

   /* Search by area code or city, 300 ms after typing stops. */
   useEffect(() => {
      const q = query.trim()
      if (!q) {
         setResults(null)
         setStaged([])
         return undefined
      }
      setSearching(true)
      const t = window.setTimeout(() => {
         void campaignFlowService
            .availableNumbers(q)
            .catch(() => [] as IPhoneNumberOption[])
            .then((r) => {
               setResults(r)
               setSearching(false)
            })
      }, 300)
      return () => window.clearTimeout(t)
   }, [query])

   /** Tick an area code: add a number there; untick: drop its numbers. */
   const toggleCode = async (code: string) => {
      if (selected.some((n) => n.areaCode === code))
         return keep((n) => n.areaCode !== code)
      if (adding.includes(code)) return
      setAdding((x) => [...x, code])
      setSoldOut((x) => x.filter((c) => c !== code))
      const n = await findFor(code, owned, selected)
      setAdding((x) => x.filter((c) => c !== code))
      if (n) add([n])
      else setSoldOut((x) => [...x, code])
   }
   const remove = (n: IPhoneNumberOption) => keep((s) => s.number !== n.number)
   const stage = (n: IPhoneNumberOption) =>
      setStaged((x) =>
         isStaged(n) ? x.filter((s) => s.number !== n.number) : [...x, n]
      )
   /* Add what was ticked, then clear the search so "Your numbers" shows them. */
   const addStaged = () => {
      add(staged)
      setQuery('')
   }
   const toBuy = selected.filter((n) => !n.owned).length
   const q = query.trim().toLowerCase().replace(/[()]/g, '')
   /* Numbers you already own in the searched area code or city, offered first. */
   const ownedHere = q
      ? owned.filter(
           (n) =>
              !isOn(n) &&
              (n.areaCode.startsWith(q) || n.city.toLowerCase().includes(q))
        )
      : []
   const shareOf = (code: string) =>
      codes?.find((c) => c.code === code)?.leadShare
   /* Leads counted for this audience, or the shares the other campaigns see. */
   const counted = !!codes?.length && codes[0].leads !== undefined
   const elsewhere = codes?.length
      ? Math.max(0, 100 - codes.reduce((a, c) => a + c.leadShare, 0))
      : 0
   /* Owned numbers the area rows do not offer: other codes, or a second one in a ticked code. */
   const spareOwned = owned.filter(
      (n) =>
         !isOn(n) &&
         (!codes?.some((c) => c.code === n.areaCode) ||
            selected.some((s) => s.areaCode === n.areaCode))
   )

   const Tone = ({ owned: isOwned }: { owned?: boolean }) => (
      <span
         className={cn(
            'text-[11px] font-semibold px-2 h-5 rounded-full flex items-center shrink-0',
            isOwned
               ? 'bg-[#00d857]/15 text-[#5BE39A]'
               : 'bg-[#9061DA]/20 text-[#DAC2FF]'
         )}
      >
         {isOwned ? 'You own this' : 'New'}
      </span>
   )

   const Row = ({
      n,
      on,
      onToggle,
      note,
      tone,
   }: {
      n: IPhoneNumberOption
      on: boolean
      onToggle: () => void
      note?: string
      tone?: 'owned' | 'new'
   }) => (
      <button
         type="button"
         onClick={onToggle}
         className="w-full flex items-center gap-3 px-4 h-12 border-b border-white/[0.06] last:border-0 text-left hover:bg-white/[0.03]"
         data-testid={`cf-number-${n.number.replace(/\D/g, '')}`}
      >
         <Checkbox on={on} />
         <span className="w-[130px] text-sm font-semibold">{n.number}</span>
         <span className="flex-1 text-[13px] text-[#9ca3af] truncate">
            {n.city}
         </span>
         {tone && <Tone owned={tone === 'owned'} />}
         {note && <span className="text-xs text-[#818181]">{note}</span>}
      </button>
   )

   return (
      <>
         <Body>
            <StepHeading
               icon="pin"
               title="Phone numbers"
               lede="Local numbers get answered more. Tick the areas your leads live in; numbers you already own come first."
            />

            {/* 1. Where your leads are: one row per area code, tick to add a number there */}
            {codes === null ? (
               <div
                  className="rounded-xl bg-white/[0.04] border border-white/[0.06] px-4 h-12 flex items-center gap-2 text-sm text-[#9ca3af]"
                  data-testid="cf-area-loading"
               >
                  <Spinner size={14} /> Finding where your leads live…
               </div>
            ) : codes.length > 0 ? (
               <div
                  className="rounded-xl bg-white/[0.04] border border-white/[0.06]"
                  data-testid="cf-area-suggestions"
               >
                  <div className="px-4 h-9 flex items-center gap-2 text-xs text-[#9ca3af] border-b border-white/[0.06]">
                     Where your leads are
                     <span className="ml-auto truncate">
                        {counted
                           ? 'Counted from this campaign’s leads'
                           : 'From your other campaigns'}
                     </span>
                  </div>
                  {codes.map((c) => {
                     const mine = selected.filter((n) => n.areaCode === c.code)
                     const on = mine.length > 0
                     const waiting = adding.includes(c.code)
                     const spare = on
                        ? undefined
                        : numberForCode(c.code, owned, selected)
                     return (
                        <button
                           key={c.code}
                           type="button"
                           onClick={() => void toggleCode(c.code)}
                           disabled={waiting}
                           aria-pressed={on}
                           className="w-full flex flex-wrap sm:flex-nowrap items-center gap-x-3 gap-y-1 px-4 py-2.5 sm:py-0 sm:h-12 border-b border-white/[0.06] last:border-0 text-left hover:bg-white/[0.03] disabled:opacity-70"
                           data-testid={`cf-area-${c.code}`}
                        >
                           <Checkbox on={on} />
                           <span className="text-sm font-semibold shrink-0">
                              ({c.code})
                           </span>
                           <span className="flex-1 min-w-0 text-[13px] text-[#9ca3af] truncate">
                              {c.city}
                           </span>
                           <span
                              className="text-xs text-[#9ca3af] shrink-0 sm:w-[96px] sm:text-right"
                              data-testid={`cf-area-${c.code}-share`}
                           >
                              {c.leadShare}% of leads
                           </span>
                           <span className="basis-full sm:basis-auto sm:w-[210px] flex items-center justify-start sm:justify-end gap-2 text-xs pl-8 sm:pl-0">
                              {waiting ? (
                                 <Spinner size={12} />
                              ) : on ? (
                                 <>
                                    <span className="font-semibold text-white">
                                       {mine[0].number}
                                       {mine.length > 1
                                          ? ` +${mine.length - 1}`
                                          : ''}
                                    </span>
                                    <Tone owned={mine[0].owned} />
                                 </>
                              ) : soldOut.includes(c.code) ? (
                                 <span className="text-amber-300">
                                    None for sale right now
                                 </span>
                              ) : spare ? (
                                 <span className="text-[#5BE39A]">
                                    Use {spare.number} · you own it
                                 </span>
                              ) : (
                                 <span className="text-[#818181]">
                                    New number · {money(price)}/month
                                 </span>
                              )}
                           </span>
                        </button>
                     )
                  })}
                  {elsewhere > 0 && (
                     <div
                        className="px-4 h-9 flex items-center text-xs text-[#818181]"
                        data-testid="cf-area-elsewhere"
                     >
                        Other areas · {elsewhere}% of leads
                     </div>
                  )}
               </div>
            ) : null}

            {/* 2. Your numbers: what the campaign calls and texts from */}
            <div
               className="rounded-xl bg-white/[0.04] border border-white/[0.06]"
               data-testid="cf-numbers"
            >
               <div className="px-4 h-9 flex items-center gap-2 text-xs text-[#9ca3af] border-b border-white/[0.06]">
                  Your numbers
                  <span className="ml-auto" data-testid="cf-numbers-cost">
                     {plural(selected.length, 'number')} ·{' '}
                     {toBuy
                        ? `${toBuy} new at ${money(price)}/month each`
                        : 'nothing new to buy'}
                  </span>
               </div>
               {selected.length ? (
                  selected.map((n) => (
                     <Row
                        key={n.number}
                        n={n}
                        on
                        onToggle={() => remove(n)}
                        tone={n.owned ? 'owned' : 'new'}
                        note={
                           shareOf(n.areaCode) !== undefined
                              ? `${shareOf(n.areaCode)}% of leads here`
                              : undefined
                        }
                     />
                  ))
               ) : (
                  <div className="px-4 py-4 text-sm text-[#9ca3af]">
                     None yet. Tick an area above, or search below.
                  </div>
               )}
            </div>

            {/* 3. Search: new numbers and ones you own, in any area */}
            <label className="flex items-center gap-2 h-11 px-4 rounded-full bg-white/[0.06] border border-white/[0.12] focus-within:border-[#9061DA]">
               <FlowIcon name="search" size={15} className="text-[#9ca3af]" />
               <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Add a number elsewhere: area code or city, e.g. 303 or Denver"
                  className="flex-1 min-w-0 bg-transparent outline-none text-sm"
                  data-testid="cf-number-search"
               />
               {searching && <Spinner size={14} />}
               {query && (
                  <button
                     type="button"
                     onClick={() => setQuery('')}
                     aria-label="Clear search"
                     className="text-white/50 hover:text-white"
                  >
                     <FlowIcon name="x" size={14} />
                  </button>
               )}
            </label>
            {results && (
               <div
                  className="rounded-xl bg-white/[0.04] border border-[#9061DA]/40"
                  data-testid="cf-number-results"
               >
                  <div className="px-4 h-9 flex items-center text-xs text-[#9ca3af] border-b border-white/[0.06]">
                     {results.length || ownedHere.length
                        ? `${plural(ownedHere.length, 'number')} you own · ${plural(results.filter((n) => !isOn(n)).length, 'new number')} available`
                        : 'No numbers found. Try a nearby area code.'}
                  </div>
                  {ownedHere.map((n) => (
                     <Row
                        key={n.number}
                        n={n}
                        on={isStaged(n)}
                        onToggle={() => stage(n)}
                        tone="owned"
                        note="free"
                     />
                  ))}
                  {results
                     .filter((n) => !isOn(n))
                     .map((n) => (
                        <Row
                           key={n.number}
                           n={n}
                           on={isStaged(n)}
                           onToggle={() => stage(n)}
                           tone="new"
                           note={`${money(price)}/month`}
                        />
                     ))}
                  <div className="px-3 py-2.5 border-t border-white/[0.06] flex items-center gap-3">
                     <span className="text-xs text-[#9ca3af]">
                        {staged.length
                           ? `${plural(staged.length, 'number')} ticked`
                           : 'Tick the numbers you want'}
                     </span>
                     <button
                        type="button"
                        onClick={addStaged}
                        disabled={!staged.length}
                        className="ml-auto inline-flex items-center gap-1.5 h-9 px-4 rounded-full bg-[#9061DA] hover:bg-[#7044B4] disabled:bg-white/10 disabled:text-white/40 text-[13px] font-semibold"
                        data-testid="cf-number-add"
                     >
                        <FlowIcon name="plus" size={13} />
                        {staged.length
                           ? `Add ${plural(staged.length, 'number')}`
                           : 'Add'}
                     </button>
                  </div>
               </div>
            )}

            {/* 4. Other numbers you own, one click to add (the rest through the search) */}
            {spareOwned.length > 0 && (
               <div className="flex flex-wrap items-center gap-2">
                  <span className="text-[13px] text-[#9ca3af] mr-1">
                     Also yours
                  </span>
                  {spareOwned.slice(0, 6).map((n) => (
                     <button
                        key={n.number}
                        type="button"
                        onClick={() => add([n])}
                        className="flex items-center gap-1.5 h-8 px-3 rounded-full border border-white/[0.14] text-[13px] font-semibold text-[#9ca3af] hover:text-white"
                        data-testid={`cf-owned-${n.areaCode}`}
                     >
                        <FlowIcon name="plus" size={12} />
                        {n.number}
                     </button>
                  ))}
                  {spareOwned.length > 6 && (
                     <span className="text-[13px] text-[#818181]">
                        and {spareOwned.length - 6} more · search above
                     </span>
                  )}
               </div>
            )}
         </Body>
         <StepFooter
            primary={selected.length ? 'Done' : 'Pick at least one number'}
            disabled={!selected.length}
            onPrimary={onDone}
            busy={busy}
            testId="cf-numbers-done"
         />
      </>
   )
}

/* ---------- Launch ---------- */

const HANDOFF_SHORT: Record<TRoutingMode, string> = {
   round_robin: 'Team, in turns',
   first_to_answer: 'Team, first to pick up',
}

export const LaunchStep = ({
   onBack,
   onLaunch,
   onGo,
   onTest,
   onSaveDraft,
   busy,
}: {
   onBack: () => void
   onLaunch: () => void
   onGo: (key: TRailKey) => void
   onTest: () => void
   /** Keeps everything as a draft and leaves; launching can wait. */
   onSaveDraft?: () => void
   busy: boolean
}) => {
   const draft = useCampaignFlowStore((s) => s.draft)
   const [result, setResult] = useState<{
      ready: boolean
      items: IReadinessItem[]
      costs: ICostLine[]
   } | null>(null)
   useEffect(() => {
      void campaignFlowService
         .review(draft)
         .catch((e: Error) => ({
            ready: false,
            items: [
               {
                  key: 'launch' as TRailKey,
                  ok: false,
                  label:
                     e?.message || 'Could not check the campaign. Try again.',
               },
            ],
            costs: [],
         }))
         .then(setResult)
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [])
   const a = draft.audience
   const c = counts(draft.playbook.touches)
   const rows: { key: TRailKey; label: string; value: string }[] = [
      {
         key: 'who',
         label: 'Calling',
         value: `${(a.leadCount ?? 0).toLocaleString()} ${a.name.toLowerCase()}`,
      },
      {
         key: 'handoff',
         label: 'Hot leads',
         value:
            draft.handoff.agentIds.length === 1
               ? 'Your phone, every time'
               : HANDOFF_SHORT[draft.handoff.mode],
      },
      { key: 'voice', label: 'Voice', value: draft.voice.name },
      {
         key: 'playbook',
         label: 'Playbook',
         value: `${cadenceSummary(draft.playbook)} · ${plural(c.calls, 'call')}, ${plural(c.texts, 'text')}`,
      },
      {
         key: 'numbers',
         label: 'Numbers',
         value:
            draft.numbers.selected.map((n) => n.number).join(', ') ||
            'None yet',
      },
   ]
   const ready = !!result?.ready
   const missing = result?.items.filter((i) => !i.ok) ?? []
   /* A running campaign is not launched again: its changes are saved. */
   const running = draft.status === 'active'

   return (
      <>
         <Body>
            <StepHeading
               icon="rocket"
               title={running ? 'Your changes' : 'Ready to launch'}
               lede={
                  running
                     ? 'Saved changes apply from the next call or text.'
                     : 'You can pause or change anything later.'
               }
            />
            <div
               className="rounded-xl bg-white/[0.04] border border-white/[0.06]"
               data-testid="cf-launch-summary"
            >
               {rows.map((r) => (
                  <div
                     key={r.key}
                     className="flex items-center gap-3 px-4 h-12 border-b border-white/[0.06] last:border-0 text-sm"
                  >
                     <span className="w-[86px] shrink-0 text-[13px] text-[#9ca3af]">
                        {r.label}
                     </span>
                     <span className="flex-1 min-w-0 truncate font-semibold">
                        {r.value}
                     </span>
                     <button
                        type="button"
                        onClick={() => onGo(r.key)}
                        className="text-[13px] font-semibold text-[#DAC2FF] hover:text-white"
                     >
                        Edit
                     </button>
                  </div>
               ))}
            </div>
            {missing.length > 0 && (
               <div
                  className="rounded-xl border border-amber-400/35 bg-amber-400/[0.08] px-4 py-2.5 text-[13px] text-amber-400 flex flex-col gap-1"
                  data-testid="cf-not-ready"
               >
                  {missing.map((i) => (
                     <button
                        key={i.key}
                        type="button"
                        onClick={() => onGo(i.key)}
                        className="flex items-center gap-2 text-left"
                     >
                        <FlowIcon name="info" size={14} /> {i.fix || i.label}{' '}
                        <span className="underline font-semibold">Fix</span>
                     </button>
                  ))}
               </div>
            )}
            <div className="flex flex-wrap items-center justify-between gap-3 text-[13px] text-[#9ca3af]">
               <span data-testid="cf-cost">
                  {result ? (
                     result.costs
                        .map((x) => `${x.label}: ${x.amount}`)
                        .join(' · ')
                  ) : (
                     <Spinner size={12} />
                  )}
               </span>
               <button
                  type="button"
                  onClick={onTest}
                  className="inline-flex items-center gap-1.5 font-semibold text-[#DAC2FF] hover:text-white"
                  data-testid="cf-launch-test"
               >
                  <FlowIcon name="phone" size={13} /> Try it as a lead first
               </button>
            </div>
         </Body>
         <StepFooter
            onBack={onBack}
            left={
               onSaveDraft ? (
                  <div className="flex items-center gap-2 shrink-0">
                     <button
                        type="button"
                        onClick={onBack}
                        className="sm:w-[120px] h-12 rounded-full bg-white/[0.08] hover:bg-white/[0.12] flex items-center justify-center gap-2 text-[15px] font-semibold"
                        data-testid="cf-back"
                     >
                        <FlowIcon name="arrowLeft" size={16} /> Back
                     </button>
                     <button
                        type="button"
                        onClick={onSaveDraft}
                        disabled={busy}
                        className="h-12 px-4 rounded-full text-[14px] font-semibold text-[#DAC2FF] hover:text-white hover:bg-white/[0.06]"
                        data-testid="cf-save-draft"
                     >
                        Save draft · finish later
                     </button>
                  </div>
               ) : undefined
            }
            primary={
               running
                  ? 'Save changes'
                  : ready
                    ? 'Launch campaign'
                    : 'Fix the item above to launch'
            }
            disabled={running ? !result : !ready}
            variant="launch"
            onPrimary={onLaunch}
            busy={busy}
            busyLabel={
               running
                  ? 'Saving your changes…'
                  : 'Launching — switching your campaign on…'
            }
            testId="cf-activate"
         />
      </>
   )
}

/* ---------- Launching ---------- */

export const LaunchingStep = ({
   leads,
   onDone,
}: {
   leads: number
   onDone: () => void
}) => {
   const steps = [
      'Saving',
      'Setting up your numbers',
      `Loading ${leads.toLocaleString()} leads`,
      'Scheduling the first calls',
   ]
   const [n, setN] = useState(0)
   useEffect(() => {
      const t = window.setTimeout(
         () => (n >= steps.length ? onDone() : setN((x) => x + 1)),
         n >= steps.length ? 500 : 650
      )
      return () => window.clearTimeout(t)
   }, [n, onDone, steps.length])
   return (
      <div
         className="flex flex-col items-center justify-center gap-6 py-16"
         data-testid="cf-launching"
      >
         <StepHeading icon="rocket" title="Launching" />
         <div className="flex flex-col gap-3 w-[280px]">
            {steps.map((s, i) => (
               <div
                  key={s}
                  className={cn(
                     'flex items-center gap-3 text-[15px]',
                     i > n && 'text-white/40'
                  )}
               >
                  {i < n ? (
                     <span className="w-6 h-6 rounded-full bg-[#00d857] text-[#18191d] flex items-center justify-center">
                        <FlowIcon name="check" size={13} strokeWidth={2.6} />
                     </span>
                  ) : i === n ? (
                     <span className="cf-spin w-6 h-6 rounded-full border-2 border-[#9061DA] border-t-transparent" />
                  ) : (
                     <span className="w-6 h-6 rounded-full border-2 border-white/20" />
                  )}
                  {s}
               </div>
            ))}
         </div>
      </div>
   )
}
